import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { LoaderService } from '../services/loader.service';
import { OrderDraftService } from '../services/order-draft.service';
import {
    StaffQuotationDetail,
    StaffQuotationService
} from '../services/staff-quotation.service';
import {
    canPunchQuotation,
    canRejectQuotation,
    statusLabel,
    toSeedLines
} from './quotation.utils';
import { QuotationStatusBadgeComponent } from './components/quotation-shared.components';

@Component({
    selector: 'app-quotation-review-detail',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        DatePipe,
        FormsModule,
        RouterModule,
        ButtonModule,
        ToolbarModule,
        ToastModule,
        TextareaModule,
        DialogModule,
        QuotationStatusBadgeComponent
    ],
    providers: [MessageService],
    templateUrl: './quotation-review-detail.component.html',
    styleUrl: './quotation-review-detail.component.scss'
})
export class QuotationReviewDetailComponent implements OnInit {
    quotationNumber = '';
    detail: StaffQuotationDetail | null = null;

    rejectDialogVisible = false;
    rejectMessage = '';

    scope = { companyId: '', divisionId: '', whsId: '' };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly inMemoryStorage: InMemoryStorageService,
        private readonly quotationService: StaffQuotationService,
        private readonly messageService: MessageService,
        private readonly loaderService: LoaderService,
        private readonly orderDraftService: OrderDraftService
    ) {}

    ngOnInit(): void {
        this.scope = {
            companyId: this.inMemoryStorage.get('company') || '',
            divisionId: this.inMemoryStorage.get('division') || '',
            whsId:
                this.inMemoryStorage.get('warehouseId') ||
                this.inMemoryStorage.get('whsId') ||
                this.inMemoryStorage.get('warehouse') ||
                ''
        };
        this.quotationNumber = this.route.snapshot.paramMap.get('quotationNumber') || '';
        if (this.quotationNumber) {
            this.load();
        } else {
            this.backToList();
        }
        this.route.paramMap.subscribe((params) => {
            const q = params.get('quotationNumber') || '';
            if (q && q !== this.quotationNumber) {
                this.quotationNumber = q;
                this.load();
            }
        });
    }

    backToList(): void {
        this.router.navigate(['/quotation-review']);
    }

    get statusText(): string {
        return statusLabel(this.detail?.status || '');
    }

    get canPunch(): boolean {
        return canPunchQuotation(this.detail);
    }

    get canReject(): boolean {
        return canRejectQuotation(this.detail);
    }

    load(): void {
        if (!this.quotationNumber) {
            this.backToList();
            return;
        }
        this.detail = null;
        this.loaderService.show('Loading draft order...', 'Please wait...');
        this.quotationService
            .getQuotation(this.quotationNumber, this.scope)
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (detail) => {
                    this.detail = detail;
                    if (canPunchQuotation(detail)) {
                        this.markUnderReviewIfNeeded();
                    }
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Failed to load draft order',
                        detail: err?.error?.message || err?.error || 'Please retry.'
                    });
                }
            });
    }

    punchAsOrder(): void {
        if (!this.detail || !this.canPunch) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Not available',
                detail: 'This draft order cannot be processed.'
            });
            return;
        }
        localStorage.removeItem('orders');
        localStorage.removeItem('orderLines');
        localStorage.removeItem('routeParams');
        this.orderDraftService.clearDraft();
        const draftId = this.createDraftId();
        localStorage.setItem('routeParams', JSON.stringify(draftId));
        this.router.navigate(['/item-v2', draftId], {
            state: {
                punchFromQuotation: true,
                quotationNumber: this.detail.quotationNumber,
                customerId: this.detail.customerId,
                customerName: this.detail.customerName,
                warehouse: this.detail.warehouse || this.scope.whsId,
                seedLines: toSeedLines(this.detail.lines)
            }
        });
    }

    openRejectDialog(): void {
        this.rejectMessage = '';
        this.rejectDialogVisible = true;
    }

    confirmReject(): void {
        if (!this.detail) return;
        const msg = (this.rejectMessage || '').trim();
        if (msg.length < 5) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Message required',
                detail: 'Enter a short reason for the customer (min 5 characters).'
            });
            return;
        }
        this.loaderService.show('Rejecting draft order...', 'Please wait...');
        this.quotationService
            .updateStatus(this.detail.quotationNumber, this.scope, {
                status: 'REJECTED',
                customerMessage: msg
            })
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (detail) => {
                    this.detail = detail;
                    this.rejectDialogVisible = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Draft order rejected',
                        detail: detail.quotationNumber
                    });
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Reject failed',
                        detail: err?.error?.message || err?.error || 'Please retry.'
                    });
                }
            });
    }

    private markUnderReviewIfNeeded(): void {
        if (!this.detail || (this.detail.status || '').toUpperCase() !== 'SUBMITTED') {
            return;
        }
        this.quotationService
            .updateStatus(this.detail.quotationNumber, this.scope, {
                status: 'UNDER_REVIEW',
                internalComment: 'Opened for review'
            })
            .subscribe({
                next: (detail) => {
                    this.detail = detail;
                },
                error: () => {
                    /* non-blocking */
                }
            });
    }

    private createDraftId(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let id = '';
        for (let i = 0; i < 5; i++) {
            id += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return id;
    }
}
